import React from 'react'

import { Dropdown, type DropdownOption } from '@/components/ui'
import { useApi, useCompany } from '@/hooks'
import type { AlertConfig } from '@/types/dashboard-types'

interface Params {
  companyId?: number
  alertConfigIds?: string[]
  acknowledgementOnly?: boolean
  disableUserSubscriptionFilter?: boolean
  onChange: (alertConfigIds?: string[]) => void
}

export const AlertNameDropdown: React.FC<Params> = ({
  alertConfigIds,
  companyId,
  acknowledgementOnly,
  disableUserSubscriptionFilter,
  onChange,
}) => {
  const { api } = useApi()
  const [options, setOptions] = React.useState<DropdownOption[]>([])
  const [placeholder, setPlaceholder] = React.useState('')
  const { companies } = useCompany()
  const companyNameById = React.useMemo(() => new Map(companies.map((c) => [c.id, c.name])), [companies])

  React.useEffect(() => {
    if (!api) {
      return
    }
    if (!companyId) {
      setOptions([])
      setPlaceholder('Choose a company to load alerts...')
      return
    }
    const fn = async () => {
      setPlaceholder('Loading...')
      const response = await api.get<AlertConfig[]>({
        path: `/fresh/basic-alert-config?companyId=${companyId}&skip=0&take=10000&deleted=false${
          disableUserSubscriptionFilter ? '&disableUserSubscriptionFilter=true' : ''
        }`,
        origin: 'AlertNameDropdown',
      })
      if (response.error || !response.data) {
        console.error(response.error)
        setPlaceholder('Error loading alerts. Contact support.')
        setOptions([])
      } else {
        const source = acknowledgementOnly ? response.data.filter((a) => a.enabledAlertAcknowledgement) : response.data
        const hasMultipleCompanies = new Set(source.map((item) => item.companyId)).size > 1
        setPlaceholder('Choose one or more alerts...')
        setOptions(
          source.map((a) => {
            const companyName = a.companyId ? companyNameById.get(a.companyId) : undefined
            return {
              label: hasMultipleCompanies && companyName ? `${a.alertName} - ${companyName}` : a.alertName,
              value: String(a.alertId),
            }
          })
        )
      }
    }
    fn()
  }, [companyId, acknowledgementOnly, disableUserSubscriptionFilter])

  return (
    <div>
      <label>Alerts</label>
      <Dropdown
        options={options}
        handleMultiSelectItem={(opt: DropdownOption[]) => {
          const selectedValues = opt.filter((option) => option.value !== '').map((option) => option.value)
          const alertConfigIds = selectedValues.length > 0 ? selectedValues : undefined
          onChange(alertConfigIds)
        }}
        selectedItem={options.filter((o) => Array.isArray(alertConfigIds) && alertConfigIds.includes(o.value))}
        placeholder={placeholder}
        animateDots={/^Loading/.test(placeholder)}
        isMultiSelect
      />
    </div>
  )
}
